import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { flushSync } from 'react-dom'

const THEME_STORAGE_KEY = 'erp_theme_mode'
const LIGHT_THEME = 'light'
const DARK_THEME = 'dark'
const THEME_TRANSITION_MS = 320
const RAPID_TRANSITION_MS = 110
const RAPID_TOGGLE_SETTLE_MS = 120

const ThemeModeContext = createContext(null)

const readStoredTheme = () => {
	if (typeof window === 'undefined') return LIGHT_THEME

	try {
		return window.localStorage.getItem(THEME_STORAGE_KEY) === DARK_THEME
			? DARK_THEME
			: LIGHT_THEME
	} catch (error) {
		return LIGHT_THEME
	}
}

export const ThemeModeProvider = ({ children }) => {
	const [themeMode, setThemeMode] = useState(readStoredTheme)
	const transitionTimeoutRef = useRef(null)
	const toggleFrameRef = useRef(null)
	const toggleDelayRef = useRef(null)
	const pendingTogglesRef = useRef(0)
	const lastToggleAtRef = useRef(-Infinity)
	const rapidBurstRef = useRef(false)
	const viewTransitionRef = useRef(null)

	useLayoutEffect(() => {
		const root = document.documentElement
		root.setAttribute('data-theme', themeMode)
		root.style.colorScheme = themeMode

		const themeColor = document.querySelector('meta[name="theme-color"]')
		if (themeColor) {
			themeColor.setAttribute('content', themeMode === DARK_THEME ? '#202124' : '#ffffff')
		}

		try {
			window.localStorage.setItem(THEME_STORAGE_KEY, themeMode)
		} catch (error) {
			// El tema continúa funcionando durante la sesión si el almacenamiento no está disponible.
		}
	}, [themeMode])

	useEffect(() => {
		const syncThemeFromAnotherTab = (event) => {
			if (event.key !== THEME_STORAGE_KEY) return
			setThemeMode(event.newValue === DARK_THEME ? DARK_THEME : LIGHT_THEME)
		}

		window.addEventListener('storage', syncThemeFromAnotherTab)
		return () => window.removeEventListener('storage', syncThemeFromAnotherTab)
	}, [])

	useEffect(() => () => {
		window.clearTimeout(transitionTimeoutRef.current)
		window.clearTimeout(toggleDelayRef.current)
		window.cancelAnimationFrame(toggleFrameRef.current)
		const activeViewTransition = viewTransitionRef.current
		if (activeViewTransition && typeof activeViewTransition.skipTransition === 'function') {
			activeViewTransition.skipTransition()
		}
		viewTransitionRef.current = null
		document.documentElement.classList.remove('theme-transitioning', 'theme-transitioning-rapid')
	}, [])

	const commitPendingToggle = useCallback(() => {
		toggleFrameRef.current = null
		const shouldToggle = pendingTogglesRef.current % 2 === 1
		pendingTogglesRef.current = 0
		if (!shouldToggle) {
			rapidBurstRef.current = false
			return
		}

		const root = document.documentElement
		const now = window.performance.now()
		const prefersReducedMotion = typeof window.matchMedia === 'function'
			&& window.matchMedia('(prefers-reduced-motion: reduce)').matches
		window.clearTimeout(transitionTimeoutRef.current)
		transitionTimeoutRef.current = null

		const useFullTransition = !prefersReducedMotion
			&& !rapidBurstRef.current
			&& now - lastToggleAtRef.current >= THEME_TRANSITION_MS
		const supportsViewTransition = typeof document.startViewTransition === 'function'
		const applyThemeChange = () => setThemeMode((currentMode) => (
			currentMode === DARK_THEME ? LIGHT_THEME : DARK_THEME
		))
		const applyLegacyTransition = (className, duration) => {
			root.classList.remove('theme-transitioning', 'theme-transitioning-rapid')
			root.classList.add(className)
			transitionTimeoutRef.current = window.setTimeout(() => {
				root.classList.remove(className)
				transitionTimeoutRef.current = null
			}, duration)
		}

		if (useFullTransition && supportsViewTransition) {
			root.classList.remove('theme-transitioning', 'theme-transitioning-rapid')
			let themeChangeCommitted = false
			try {
				const viewTransition = document.startViewTransition(() => {
					flushSync(() => {
						applyThemeChange()
						themeChangeCommitted = true
					})
				})
				viewTransitionRef.current = viewTransition
				const clearViewTransition = () => {
					if (viewTransitionRef.current === viewTransition) {
						viewTransitionRef.current = null
					}
				}
				viewTransition.finished.then(clearViewTransition, clearViewTransition)
			} catch (error) {
				if (!themeChangeCommitted) {
					applyLegacyTransition('theme-transitioning', THEME_TRANSITION_MS)
					applyThemeChange()
				}
			}
		} else if (supportsViewTransition) {
			root.classList.remove('theme-transitioning', 'theme-transitioning-rapid')
			applyThemeChange()
		} else if (useFullTransition) {
			applyLegacyTransition('theme-transitioning', THEME_TRANSITION_MS)
			applyThemeChange()
		} else if (!prefersReducedMotion) {
			applyLegacyTransition('theme-transitioning-rapid', RAPID_TRANSITION_MS)
			applyThemeChange()
		} else {
			root.classList.remove('theme-transitioning')
			root.classList.remove('theme-transitioning-rapid')
			applyThemeChange()
		}
		rapidBurstRef.current = false
		lastToggleAtRef.current = now
	}, [])

	const toggleThemeMode = useCallback(() => {
		pendingTogglesRef.current += 1
		const hasPendingWork = toggleFrameRef.current !== null || toggleDelayRef.current !== null
		const elapsed = window.performance.now() - lastToggleAtRef.current
		const isRapidToggle = hasPendingWork || elapsed < THEME_TRANSITION_MS

		if (isRapidToggle) {
			rapidBurstRef.current = true
			if (toggleFrameRef.current !== null) {
				window.cancelAnimationFrame(toggleFrameRef.current)
				toggleFrameRef.current = null
			}
			window.clearTimeout(transitionTimeoutRef.current)
			transitionTimeoutRef.current = null
			const root = document.documentElement
			const activeViewTransition = viewTransitionRef.current
			if (activeViewTransition && typeof activeViewTransition.skipTransition === 'function') {
				activeViewTransition.skipTransition()
			}
			viewTransitionRef.current = null
			root.classList.remove('theme-transitioning', 'theme-transitioning-rapid')
			window.clearTimeout(toggleDelayRef.current)
			toggleDelayRef.current = window.setTimeout(() => {
				toggleDelayRef.current = null
				toggleFrameRef.current = window.requestAnimationFrame(commitPendingToggle)
			}, RAPID_TOGGLE_SETTLE_MS)
			return
		}

		toggleFrameRef.current = window.requestAnimationFrame(commitPendingToggle)
	}, [commitPendingToggle])

	const value = useMemo(() => ({
		themeMode,
		isDarkMode: themeMode === DARK_THEME,
		toggleThemeMode,
	}), [themeMode, toggleThemeMode])

	return (
		<ThemeModeContext.Provider value={value}>
			{children}
		</ThemeModeContext.Provider>
	)
}

export const useThemeMode = () => {
	const context = useContext(ThemeModeContext)

	if (!context) {
		throw new Error('useThemeMode debe utilizarse dentro de ThemeModeProvider')
	}

	return context
}
